import React, { useState, useEffect } from 'react';
import { Cookie, Settings, Check, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { initGA, disableGA } from '../utils/analytics';
import './CookiesModal.css';

// Глобальная функция для открытия настроек cookies из других компонентов
let openCookiesSettingsCallback = null;

export function openCookiesSettings() {
  if (openCookiesSettingsCallback) {
    openCookiesSettingsCallback();
  }
}

function CookiesModal() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [forceShow, setForceShow] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Обязательные cookie (всегда включены)
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Регистрируем callback для открытия настроек из других компонентов
    openCookiesSettingsCallback = () => {
      localStorage.removeItem('cookieConsent');
      setForceShow(true);
      setIsVisible(true);
      setShowSettings(true);
    };

    return () => {
      openCookiesSettingsCallback = null;
    };
  }, []);

  useEffect(() => {
    // Если принудительный показ, сразу показываем настройки
    if (forceShow) {
      setIsVisible(true);
      setShowSettings(true);
      return;
    }

    // Проверяем, было ли уже дано согласие
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setIsVisible(true);
    } else {
      // Загружаем сохраненные настройки
      try {
        const saved = JSON.parse(cookieConsent);
        setCookiePreferences({
          necessary: true, // Всегда включены
          analytics: saved.analytics || false,
          marketing: saved.marketing || false
        });
        // Инициализируем Google Analytics, если согласие было дано
        if (saved.analytics === true) {
          initGA();
        }
      } catch (e) {
        // Если ошибка парсинга, используем значения по умолчанию
      }
    }
  }, [forceShow]);

  const handleAcceptAll = () => {
    const preferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setCookiePreferences({
      necessary: true,
      analytics: true,
      marketing: true
    });
    // Инициализируем Google Analytics при согласии
    initGA();
    // Уведомляем другие компоненты об изменении согласия
    window.dispatchEvent(new Event('cookieConsentChanged'));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const preferences = {
      necessary: true, // Обязательные всегда включены
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setCookiePreferences({
      necessary: true,
      analytics: false,
      marketing: false
    });
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const preferences = {
      ...cookiePreferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    // Обновляем Google Analytics в зависимости от настроек
    if (cookiePreferences.analytics) {
      initGA();
    } else {
      disableGA();
    }
    // Уведомляем другие компоненты об изменении согласия
    window.dispatchEvent(new Event('cookieConsentChanged'));
    setIsVisible(false);
  };

  const handleTogglePreference = (type) => {
    if (type === 'necessary') return; // Нельзя отключить обязательные
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleWithdrawConsent = () => {
    // Пользователь может отозвать согласие
    localStorage.removeItem('cookieConsent');
    setCookiePreferences({
      necessary: true,
      analytics: false,
      marketing: false
    });
    // Отключаем Google Analytics при отзыве согласия
    disableGA();
    // Уведомляем другие компоненты об изменении согласия
    window.dispatchEvent(new Event('cookieConsentChanged'));
    setIsVisible(true);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <div className="cookies-modal-overlay">
      <div className={`cookies-modal ${showSettings ? 'cookies-modal-expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
        {!showSettings ? (
          <div className="cookies-modal-content">
            <p className="cookies-modal-text">
              Мы используем cookies для улучшения работы сайта.
            </p>
            <button 
              className="cookies-modal-button cookies-modal-button-primary" 
              onClick={handleAcceptAll}
            >
              Хорошо, больше не показывать
            </button>
          </div>
        ) : (
          <div className="cookies-modal-content cookies-modal-settings">
            <h3>Настройки cookies</h3>
            <p className="cookies-modal-info">
              Вы можете выбрать, какие типы cookies разрешить. Обязательные cookies необходимы для работы сайта и не могут быть отключены.
            </p>

            <div className="cookies-settings-list">
              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <div>
                    <h4>Обязательные cookies</h4>
                    <p>Необходимы для работы сайта (авторизация, безопасность)</p>
                  </div>
                  <div className="cookie-toggle disabled">
                    <Check size={16} />
                  </div>
                </div>
                <p className="cookie-setting-details">
                  Эти cookies необходимы для базовой функциональности сайта и не могут быть отключены.
                </p>
              </div>

              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <div>
                    <h4>Аналитические cookies</h4>
                    <p>Помогают улучшать сайт, собирая информацию об использовании</p>
                  </div>
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.analytics}
                      onChange={() => handleTogglePreference('analytics')}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
                <p className="cookie-setting-details">
                  Используются для сбора статистики посещений и улучшения пользовательского опыта.
                </p>
              </div>

              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <div>
                    <h4>Маркетинговые cookies</h4>
                    <p>Используются для показа релевантной рекламы</p>
                  </div>
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.marketing}
                      onChange={() => handleTogglePreference('marketing')}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
                <p className="cookie-setting-details">
                  Помогают показывать рекламу, которая может быть интересна вам.
                </p>
              </div>
            </div>

            <div className="cookies-modal-buttons">
              <button 
                className="cookies-modal-button cookies-modal-button-secondary" 
                onClick={() => setShowSettings(false)}
              >
                Назад
              </button>
              <button 
                className="cookies-modal-button cookies-modal-button-primary" 
                onClick={handleSavePreferences}
              >
                Сохранить настройки
              </button>
            </div>

            <p className="cookies-modal-withdraw">
              Вы можете отозвать согласие в любое время.{' '}
              <button 
                className="cookies-withdraw-link" 
                onClick={handleWithdrawConsent}
              >
                Отозвать согласие
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CookiesModal;

